import { isWpsEnv } from '@/utils/get-user-info';
import { showMessage } from '@vas2t-lowcode/utils';
import { checkWpsEnvIsNewPage } from './env';
import { loadCommonJs2tAsync } from './load-js-to-dom';

// 检查环境是否为小程序
const wpsMinProgramEnvReady = (fn: () => unknown) => {
  const ready = () => {
    fn();
  };
  if (!window.wps) {
    document.addEventListener('WpsofficeSDKReady', ready, false);
  } else {
    fn();
  }
};

/** 显示登录弹窗 */
export function showWpsLoginAsync() {
  if (!checkWpsEnvIsNewPage()) {
    // 如果不是新建页容器, 则为小程序环境（低代码平台只有两种模式下：新建页、小程序容器（中间页））
    wpsMinProgramEnvReady(() => {
      window.wps.login();
    });
    return;
  } else {
    // common2t-js 提供
    loadCommonJs2tAsync().then(() => {
      window.common?.user?.logIn();
    });
  }
}

/**
 * 端内监听登录状态改变
 */
export const bindLoginStateChangedAsync = () => {
  const isNewPage = checkWpsEnvIsNewPage();

  if (!isNewPage) {
    // 小程序环境
    wpsMinProgramEnvReady(() =>
      window.wps.onLoginStatusChanged(() => {
        location.reload();
      })
    );
    return;
  }

  // 内部会在登录状态变更后刷新页面
  loadCommonJs2tAsync().then(() => {
    window.common?.env?.initListener();
  });
};

// #region 客户端接口

function wpsQueryApi(jsonStr: string) {
  window.wpsQuery?.({
    request: 'fromJSAsynCall("' + jsonStr + '")',
    persistent: false,
  });
}

const themeChangeEventName = 'event.skin.change';

function addEventListener(eventName: string, callbackName: string) {
  const paramsV = {
    eventName: eventName,
    callbackName: callbackName,
  };

  const JSONStr = JSON.stringify({
    method: 'common.event.addEventListener',
    params: paramsV,
  });

  wpsQueryApi(JSONStr);
}

function removeEventListener(eventName: string) {
  const paramsV = {
    eventName: eventName,
  };

  const JSONStr = JSON.stringify({
    method: 'common.event.removeEventListener',
    params: paramsV,
  });

  wpsQueryApi(JSONStr);
}

function callApi(apiName: string, callbackName: string, params: any) {
  const JSONStr = JSON.stringify({
    method: apiName,
    params: params,
    callback: callbackName,
  });

  wpsQueryApi(JSONStr);
}

function callApiNoParams(apiName: string, callbackName: string) {
  const JSONStr = JSON.stringify({
    method: apiName,
    callback: callbackName,
  });

  wpsQueryApi(JSONStr);
}

// 注册皮肤切换监听
// 传入 callbackName 回调函数名称 str
// 切换时返回数据：{"callstatus":"ok"}
export function addThemeChangedListener(callbackName: string) {
  addEventListener(themeChangeEventName, callbackName);
}

// 移除皮肤切换监听
function removeThemeChangedListener() {
  removeEventListener(themeChangeEventName);
}

// 运行应用
// 输入
// --appid 应用id str
// --src 调起来源 str
// --action 调起url str --仅小程序框架应用支持传入URL
// --callbackName 回调函数名称 str
// 返回 {"callstatus":"ok","result":"ok"}
// --callstatus
// --result
// 均为ok调用成功
export function runApp(appid: string, src: string, action: string, callbackName: string) {
  const params = {
    appId: appid,
    arguments: {
      src: src,
      action: action,
    },
  };

  callApi('runApp', callbackName, params);
}

interface IClientIsDarkSkinCb {
  callstatus: string;
  curSkin: boolean;
}

// 是否是暗黑皮肤
// 返回 {"callstatus":"ok","curSkin":true}
// --callstatus 接口调用成功
// --curSkin 是否是暗黑皮肤
export function isDarkSkinAsync() {
  const isWps = isWpsEnv();
  // 非wps端内环境或者非新建页
  if (!isWps || !checkWpsEnvIsNewPage()) {
    return Promise.resolve();
  }

  return new Promise((res) => {
    const randomFnName = `isDarkSkinCb_${new Date().valueOf()}_${Math.floor(Math.random() * 10)}`;
    window.__VAST2T_LOWCODE__[randomFnName] = (data: string) => {
      let isDark = false;
      try {
        const dataObj = JSON.parse(data) as IClientIsDarkSkinCb;
        isDark = dataObj.curSkin;
      } catch (err) {
        console.warn('isDarkSkinAsync', err);
      } finally {
        res(isDark);
      }
    };

    callApiNoParams('isDarkSkin', `__VAST2T_LOWCODE__.${randomFnName}`);
  });
}
// #endregion

/**  打开应用 */
export const openAppInNewTab = (url: string, appid: string, src: string, extraData?: Record<string, any>) => {
  if (checkWpsEnvIsNewPage()) {
    // 新建页
    runApp(appid, src, url, '');
    return;
  }

  // 小程序
  window.wps?.canILaunch({
    appId: appid,
    complete: (res: any) => {
      if (res?.data?.ok) {
        window.wps.navigateToMiniProgram({
          appid,
          extraData,
        });
      } else {
        console.warn('无法打开应用', url);
      }
    },
  });
};

/**
 * 新建页换肤监听
 *
 * @param cb
 */
export const bindThemeChange = (cb: () => void) => {
  const randomFnName = `themeChangeCb_${new Date().valueOf()}_${Math.floor(Math.random() * 10)}`;
  window.__VAST2T_LOWCODE__[randomFnName] = () => {
    cb();
  };
  addThemeChangedListener(`__VAST2T_LOWCODE__.${randomFnName}`);
};

/**
 * 新建页换肤取消监听
 *
 * @param cb
 */
export const unbindThemeChange = () => {
  removeThemeChangedListener();
};

const pageShowEventName = 'event.wpsnewapp.pageshow';
/**
 * 绑定页面显示
 *
 * @param cb
 */
const _bindPageShow = (cb: (isShow: boolean) => void) => {
  const cbName = 'pageShowEventCb';
  window[cbName] = (data) => {
    try {
      const _data = JSON.parse(data) as { eventName: string; isShow: boolean };
      cb(_data.isShow);
    } catch (err) {
      console.warn('bindPageShow', err);
    }
  };

  addEventListener(pageShowEventName, cbName);
};

/**
 * 移除页面显示监听
 *
 * @param cb
 */
export const unbindPageShow = () => {
  if (!checkWpsEnvIsNewPage()) {
    return;
  }

  removeEventListener(pageShowEventName);
};

/**
 * 新建页下切换到新建应用时需要重刷页面
 *
 * @returns
 */
export const bindPageShow = (cb: (isShow: boolean) => void) => {
  if (!checkWpsEnvIsNewPage()) {
    return;
  }

  // 绑定页面展示时刷新，只用于新建页
  _bindPageShow((isShow) => {
    cb(isShow);
  });
};

/**
 * 添加至桌面
 */
export const addToDesktop = () => {
  window.cbAddToDesktop = function (data) {
    try {
      const dataObj = JSON.parse(data);
      if (dataObj.result && dataObj.callstatus === 'ok') {
        showMessage({ msgContent: '添加成功', msgType: 'success' });
      } else {
        console.warn('添加桌面快捷图标失败');
      }
    } catch (e) {
      console.warn('addToDesktop', e);
    }
  };

  callApi('createShortcut', 'cbAddToDesktop', undefined);
};

// 序号
let isGetSrcCbSeq = 0;
/**
 * 获取客户端 src
 */
export const getSrc = (cb: (data: string) => void) => {
  isGetSrcCbSeq++;
  const cbFnCode = `cbGetSrc_${new Date().valueOf()}_${isGetSrcCbSeq}`;
  (window as any)[cbFnCode] = cb;

  // callApi('extend.util.getSrc', cbFnCode, undefined);

  const JSONStr = JSON.stringify({
    method: 'extend.util.getSrc',
    callback: cbFnCode,
  });
  wpsQueryApi(JSONStr);
};

// 序号
let isSupportCbSeq = 0;
/**
 * 判断客户端api是否支持
 *
 * @param data
 * @param cb
 * @returns
 */
export const isSupportWpsFnAsync = (data: Record<string, any>) => {
  if (!data) {
    console.error('isSupportWpsFn 参数错误，请检查');
    return Promise.resolve(false);
  }

  return new Promise<boolean>((res) => {
    isSupportCbSeq++;
    const cbFnCode = `cbIsSupport_${new Date().valueOf()}_${isSupportCbSeq}`;
    (window as any)[cbFnCode] = function (str: string) {
      try {
        const dataObj = JSON.parse(str);
        res(!!dataObj?.result);
      } catch (error) {
        res(false);
      } finally {
        // 销毁回调函数
        window.setTimeout(() => {
          (window as any)[cbFnCode] = undefined;
        }, 0);
      }
    };
    callApi('common.util.support', cbFnCode, data);
  });
};
